import { createTheme } from '@mui/material';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    bold: React.CSSProperties;
    light: React.CSSProperties;
    mono: React.CSSProperties;
    medium: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    bold?: React.CSSProperties;
    light?: React.CSSProperties;
    mono?: React.CSSProperties;
    medium?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    bold: true;
    light: true;
    mono: true;
    medium: true;
  }
}

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mobile: true; // adds the `mobile` breakpoint
    tablet: true;
    laptop: true;
    desktop: true;
  }
}

export const theme = createTheme({
  palette: {
    background: {
      default: '#FAFAFA',
    },
    primary: {
      main: '#000000',
    },
    secondary: {
      main: '#E9FF7D',
    },
    info: {
      main: '#0075FF',
    },
  },
  typography: {
    fontFamily: 'nb_international_proregular',
    fontSize: 16,
    light: {
      fontFamily: 'nb_international_prolight',
    },
    bold: {
      fontFamily: 'nb_international_probold',
    },
    mono: {
      fontFamily: 'nb_international_promono',
    },
    medium: {
      fontFamily: 'nb_international_promedium',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#0075FF',
            color: 'white',
          },
          textTransform: 'none',
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 0,
      tablet: 768,
      laptop: 1024,
      desktop: 1280,
    },
  },
});

export const mobileBreakpoint = theme.breakpoints.down('sm');

export const colors = {
  greys: {
    BRAND_BLACK: '#000000',
    TEXT_BLACK: '#242424',
    BLACK_900: '#2B2B2B',
    BORDER_GREY: '#EAECF0',
    BG_GREY: '#EDEDED',
    HOVER_GREY: '#F2F2F2',
    SUBDUED_GREY: '#C9CCCF',
    WARM_GREY: '#E7E5DB',
    DISABLED_GREY: '#F1F1F1',
    DISABLED_BORDER_GREY: '#8C9196',
    LOADING_BORDER_GREY: '#BABFC3',
    LOADING_BG_GREY: 'rgba(241, 241, 241, 0.80)',
    GREY_BROWN: 'rgba(215, 216, 210, 0.4)',
    SECONDARY_TEXT_GREY: '#494949',
    GREY_50: '#F9FAFB',
    GREY_300: '#D0D5DD',
    GREY_400: '#98A2B3',
    GREY_500: '#667085',
    GREY_550: '#676767',
    GREY_600: '#475467',
    GREY_700: '#344054',
    GREY_900: '#101828',
    BASE_WHITE: '#FFFFFF',
    BG_WHITE: '#FAFAFA',
    SILVER: '#C5C5C5',
  },
  primarys: {
    RED: '#EF562B',
    ORANGE: '#FF7C33',
    YELLOW: '#FFD233',
    GREEN: '#2AD033',
  },
  greens: {
    MID: '#AFF07B',
    HIGLIGHT: '#E9FF7D',
    BASE: '#324A3E',
    GRAY: '#D7D8D2',
    SUCCESS_100: '#DCFAE6',
    SUCCESS_500: '#17B26A',
  },
  blues: {
    MID: '#0C3DFF',
    HIGHLIGHT: '#0075FF',
    HIGHLIGHT_LIGHT: '#DEF3FF',
    BASE: '#142E62',
    GRAY: '#E2E2E2',
    BRIGHT: '#3538CD',
    LIGHT: '#E5F1FF',
    PRIMARY: '#7CB5D7',
    PRIMARY_LIGHT: '#9ECAE4',
    PRIMARY_HOVER: '#0083D0',
  },
  reds: {
    ERROR: '#ff4242',
    DESTRUCTIVE: '#EF4444',
    MEDIUM: '#B91C1C',
    DARK: '#7F1D1D',
  },
  yellows: {
    YELLOW_500: '#FEC84B',
    HIGHLIGHT: '#FFFDC5',
  },
  brand: {
    PRIMARY_RED: '#FF595A',
    DARK_RED: '#F5191A',
    PRIMARY_BLUE: '#7CB5D7',
    PRIMARY_TEXT: '#505050',
  },
};
